import { useState, useEffect } from "react";
import {
  Box, Badge, Button, Divider, Menu, MenuItem, CircularProgress, TextField,
  InputAdornment, IconButton, Tooltip, ListItemIcon, ListItemText,
  Popover, Stack, Typography, FormGroup, FormControlLabel, Checkbox,
} from "@mui/material";
import { ColumnsPanelTrigger } from "@mui/x-data-grid";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import SpeakerNotesOffOutlinedIcon from "@mui/icons-material/SpeakerNotesOffOutlined";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CustomFilters from "./CustomFilters";
import SetIpInOut from "./IpInOut";
import API from "../api";
import { palette } from "../theme";
import { toolIconSx } from "./toolbarStyles";
import { ToolIconButton, VisibilityToggle } from "./ToolbarKit";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";

const NAME_OPTIONS = [
  { value: "dns", label: "DNS Name" },
  { value: "netbios", label: "NetBIOS Name" },
  { value: "custom", label: "Custom Name" },
];

// Filtro a selezione come in Aggregations: icona + popover con checkbox (spuntato = mostrato).
// Su High/Critical le opzioni riguardano lo stato della risposta, su Alert/Movements
// la log suppression: titolo e icona diversi per non confondere i due concetti.
const SuppressionFilter = ({ title, icon: Icon, options }) => {
  const [anchor, setAnchor] = useState(null);
  // Bozza locale applicata solo con Apply: il toolbar è passato alla griglia come
  // slot inline e viene ricreato a ogni cambio di stato, quindi applicare subito
  // ogni checkbox chiuderebbe il popover a ogni click.
  const [draft, setDraft] = useState({});
  const anyHidden = options.some((o) => !o.visible);
  const changed = options.some((o) => draft[o.label] !== o.visible);
  const allSelected = options.every((o) => draft[o.label]);

  const open = (e) => {
    setDraft(Object.fromEntries(options.map((o) => [o.label, o.visible])));
    setAnchor(e.currentTarget);
  };
  const apply = () => {
    setAnchor(null);
    options.forEach((o) => { if (draft[o.label] !== o.visible) o.onToggle(); });
  };

  return (
    <>
      <Tooltip arrow title={anyHidden
        ? `${title}: hiding ${options.filter((o) => !o.visible).map((o) => o.label).join(", ")}`
        : `${title}: showing all`}>
        <IconButton size="small" sx={toolIconSx(anyHidden)} onClick={open}>
          <Badge color="primary" variant="dot" invisible={!anyHidden}>
            <Icon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Stack spacing={1} sx={{ p: 2, width: 290, maxWidth: "90vw" }}>
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          <FormGroup>
            {options.map((o) => (
              <FormControlLabel
                key={o.label}
                sx={{ alignItems: "flex-start", mx: 0, "& .MuiCheckbox-root": { pt: "3px" } }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!draft[o.label]}
                    onChange={(e) => setDraft((d) => ({ ...d, [o.label]: e.target.checked }))}
                    sx={{ color: o.accent, "&.Mui-checked": { color: o.accent } }}
                  />
                }
                label={
                  <Box>
                    <Box sx={{ fontSize: "0.875rem" }}>{o.label}</Box>
                    <Box sx={{ fontSize: 12, color: "text.secondary" }}>{o.description}</Box>
                  </Box>
                }
              />
            ))}
          </FormGroup>
          <Stack direction="row" justifyContent="space-between">
            <Button
              size="small"
              disabled={allSelected}
              onClick={() => setDraft(Object.fromEntries(options.map((o) => [o.label, true])))}
            >
              Select all
            </Button>
            <Button size="small" variant="contained" disabled={!changed} onClick={apply}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

const CustomToolbarWithButton = ({
  payload,
  rowCounter,
  hideBackup,
  setHideBackup,
  hideSuppressed,
  setHideSuppressed,
  hideDisabled,
  setHideDisabled,
  hideNotHandled,
  setHideNotHandled,
  hideHandled,
  setHideHandled,
  onOpenSuggestions,
  onOpenGrouping,
  onToggleTiresia,
  tiresiaActive = false,
  noiseCount = 0,
  noiseEnabled = false,
  onToggleNoise,
  noisePatterns,
  hideAcked,
  setHideAcked,
  setSubnetValue,
  subnetValue,
  filterModel,
  setFilterModel,
  source,
  setSource,
  destination,
  setDestination,
  isPrivateSource,
  isPrivateDestination,
  setNames,
  names,
  type
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorSubnet, setAnchorSubnet] = useState(null);
  const [anchorFilter, setAnchorFilter] = useState(null);
  const [valueString, setvalueString] = useState(subnetValue);
  const [badgeView, setBadgeView] = useState(false);
  const [loadingCSV, setLoadingCSV] = useState(false);
  const [anchorElNames, setAnchorElNames] = useState(null);

  const isResponseView = type === 0 || type === 1;
  const isLogView = type === 2 || type === 3;

  const handleClose = (format) => { setAnchorEl(null); if (format) handleExport(format); };
  const handleCloseSubnetMenu = () => { setAnchorSubnet(null); };
  const handleChangeSubnetValue = () => { setSubnetValue(valueString); handleCloseSubnetMenu(); };
  const handleClearSubnetValue = () => { setvalueString(""); setSubnetValue(""); handleCloseSubnetMenu(); };
  const handleSelectName = (name) => { setNames(name); setAnchorElNames(null); };

  const isValidSubnet = (value) => {
    const subnetRegex = /^(?:\d{1,3}\.){3}\d{1,3}\/([1-9]|[12]\d|3[0-2])$/;
    return subnetRegex.test(value);
  };

  useEffect(() => {
    const safeModel = Array.isArray(filterModel) ? filterModel : [];

    if (safeModel.length === 0) {
      setBadgeView(true);
      return;
    }

    // Un solo filtro semplice (non bracket) con value vuoto → badge nascosto
    const first = safeModel[0];
    if (
      safeModel.length === 1 &&
      !first.filters &&                          // non è un bracket
      (first.value === "" || (Array.isArray(first.value) ? first.value.length === 0 : !first.value))
    ) {
      setBadgeView(true);
      return;
    }

    setBadgeView(false);
  }, [filterModel]);

  const handleExport = async (format) => {
    setLoadingCSV(true);
    try {
      const response = await API.getSecurityLog({
        ...payload,
        pageNumber: 0,
        limit: rowCounter,
        // Cloud: su High/Critical "supp" ha un'altra semantica, quindi la soppressione
        // della risposta viaggia su hideResponseSuppressed (vedi getSecurityV4).
        ...(isResponseView
          ? { hideResponseSuppressed: hideSuppressed, hideDisabled: hideDisabled, hideNotHandled: hideNotHandled, hideHandled: hideHandled }
          : { hideSuppressed: hideSuppressed }),
        hideBackup: hideBackup,
        ...(noisePatterns ? { noisePatterns } : {}),
      });
      const allData = response.data.data;
      if (format === "csv") {
        downloadCSV(convertToCSV(allData), "exported_data.csv");
      } else if (format === "json") {
        downloadJSON(convertToFilteredJSON(allData), "exported_data.json");
      }
      setLoadingCSV(false);
    } catch (error) {
      console.error("[handleExport] Error during export:", error);
      setLoadingCSV(false);
    }
  };

  // Anti CSV-formula-injection: un valore che inizia con = + - @ (o tab/CR,
  // meno comuni ma comunque interpretati da alcuni client) viene eseguito
  // come formula da Excel/LibreOffice all'apertura del file. I dati esportati
  // qui provengono da traffico di rete/IDS potenzialmente sotto controllo di
  // un attaccante, quindi vanno trattati come input non fidato.
  const sanitizeCsvCell = (value) => {
    const str = String(value ?? "");
    const escaped = str.replace(/"/g, '""');
    return /^[=+\-@\t\r]/.test(escaped) ? `'${escaped}` : escaped;
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";
    const excludedColumns = ["fkSeriale","tipo","tipoAttacco","suppressed","disabled","backup","hostNameAtt","hostNameDest","ipAttaccante","ipDestinatario"];
    const headerTranslations = { vlan:"VLAN", protocol:"Protocol", ipSource:"Source IP", classificazione:"Classification", app_proto:"Application Protocol", dettaglio:"Detail", ipDestination:"Destination IP", timeStamp:"Timestamp", SourcePort:"Source Port", DestinationPort:"Destination Port", in_iface:"Interface", tx_id:"Transaction ID", http:"HTTP", topic:"Topic" };
    const headers = Object.keys(data[0]).filter(h => !excludedColumns.includes(h));
    const translatedHeaders = headers.map(h => headerTranslations[h] || h);
    const csvRows = [translatedHeaders.join(",")];
    data.forEach(row => { csvRows.push(headers.map(h => `"${sanitizeCsvCell(row[h])}"`).join(",")); });
    return csvRows.join("\n");
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const convertToFilteredJSON = (data) => {
    const excludedColumns = ["fkSeriale","tipo","tipoAttacco","suppressed","disabled","backup","hostNameAtt","hostNameDest","ipAttaccante","ipDestinatario"];
    return data.map(row => {
      const filteredRow = {};
      Object.keys(row).forEach(key => { if (!excludedColumns.includes(key)) filteredRow[key] = row[key]; });
      return filteredRow;
    });
  };

  const downloadJSON = (jsonContent, filename) => {
    const blob = new Blob([JSON.stringify(jsonContent, null, 2)], { type: "application/json;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const safeFilterModel = Array.isArray(filterModel) ? filterModel : [];
  const activeFilterCount = badgeView ? 0 : safeFilterModel.length;

  return (
    // Box e non Toolbar: dataGridStyle applica bordi/padding a tutti i bottoni
    // dentro .MuiToolbar-root, che qui romperebbero lo stile icon-button.
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        px: 1.5,
        py: 1,
        backgroundColor: "#292929",
        borderBottom: "1px solid #1e1e1e",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip arrow title="Columns">
          <ColumnsPanelTrigger render={<IconButton size="small" sx={toolIconSx()} />}>
            <ViewColumnOutlinedIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>

        <Tooltip arrow title={activeFilterCount ? `Filters (${activeFilterCount} active)` : "Filters"}>
          <IconButton size="small" sx={toolIconSx(activeFilterCount > 0)} onClick={(e) => setAnchorFilter(e.currentTarget)}>
            <Badge color="primary" badgeContent={activeFilterCount} invisible={!activeFilterCount}
              sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16, top: -4, right: -4 } }}>
              <FilterListIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorFilter} open={Boolean(anchorFilter)} onClose={() => setAnchorFilter(null)}>
          <CustomFilters filterModel={safeFilterModel} setFilterModel={setFilterModel} />
        </Menu>

        <Tooltip arrow title={subnetValue ? `Subnet: ${subnetValue}` : "Subnet filter"}>
          <IconButton size="small" sx={toolIconSx(!!subnetValue)} onClick={(e) => setAnchorSubnet(e.currentTarget)}>
            <Badge color="primary" variant="dot" invisible={!subnetValue}>
              <HubOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorSubnet} open={Boolean(anchorSubnet)} onClose={handleCloseSubnetMenu}>
          <MenuItem>
            <TextField
              id="subnetValue" label="Subnet Filter" variant="outlined"
              sx={{ width: "200px" }} InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleChangeSubnetValue} edge="end" disabled={!isValidSubnet(valueString)}>
                      <SaveIcon />
                    </IconButton>
                    {valueString && (
                      <IconButton onClick={handleClearSubnetValue} edge="end"><ClearIcon /></IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              name="SubnetValue" value={valueString}
              onChange={(e) => setvalueString(e.target.value)}
              error={Boolean(valueString) && !isValidSubnet(valueString)}
              helperText={valueString && !isValidSubnet(valueString) ? "Invalid subnet format (ex: 192.168.1.0/24)" : ""}
            />
          </MenuItem>
        </Menu>

        <SetIpInOut
          source={source} setSource={setSource}
          destination={destination} setDestination={setDestination}
          isPrivateSource={isPrivateSource} isPrivateDestination={isPrivateDestination}
          compact
        />

        <Tooltip arrow title={`Host names: ${NAME_OPTIONS.find(o => o.value === names)?.label ?? names}`}>
          <IconButton size="small" sx={toolIconSx()} onClick={(e) => setAnchorElNames(e.currentTarget)}>
            <BadgeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorElNames} open={Boolean(anchorElNames)} onClose={() => setAnchorElNames(null)}>
          {NAME_OPTIONS.map((o) => (
            <MenuItem key={o.value} onClick={() => handleSelectName(o.value)}>
              <ListItemIcon>{names === o.value && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText>{o.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }} aria-label="Visibility">

        {onToggleTiresia && (
          <ToolIconButton
            title={tiresiaActive
              ? "Showing only events involving hosts with Tires-IA anomalies (score above 50%, common ports, last 72h). Click to show all events."
              : "Show only events involving hosts with Tires-IA anomalies (score above 50%, common ports, last 72h)"}
            active={tiresiaActive}
            dot
            onClick={onToggleTiresia}
          >
            <AutoAwesomeIcon fontSize="small" />
          </ToolIconButton>
        )}

        {onOpenGrouping && (
          <ToolIconButton title="Top and bottom IPs by number of logs" onClick={onOpenGrouping}>
            <BarChartIcon fontSize="small" />
          </ToolIconButton>
        )}

        {onOpenSuggestions && (
          <ToolIconButton title="Noise analysis: spot recurring patterns in these logs and get suggestions on what to suppress or hide" onClick={onOpenSuggestions}>
            <TipsAndUpdatesOutlinedIcon fontSize="small" />
          </ToolIconButton>
        )}

        {onToggleNoise && (
          <ToolIconButton
            title={noiseCount === 0
              ? "Hide noise: no patterns chosen yet. Open the noise analysis and use \"Hide from view\" on a suggestion."
              : noiseEnabled
                ? `Hiding ${noiseCount} noise pattern${noiseCount > 1 ? "s" : ""} without a suppression rule. Click to show them again.`
                : `${noiseCount} noise pattern${noiseCount > 1 ? "s" : ""} chosen but shown. Click to hide them.`}
            active={noiseEnabled && noiseCount > 0}
            count={noiseEnabled ? noiseCount : 0}
            onClick={noiseCount === 0 ? onOpenSuggestions : onToggleNoise}
          >
            <CleaningServicesOutlinedIcon fontSize="small" />
          </ToolIconButton>
        )}

        {isResponseView && (
          <SuppressionFilter
            title="Response status"
            icon={PanToolOutlinedIcon}
            options={[
              {
                label: "Handled",
                accent: "#459095",
                visible: !hideHandled,
                onToggle: () => setHideHandled((prev) => !prev),
                description: "Normal procedural response",
              },
              {
                label: "Suppressed",
                accent: "#41B145",
                visible: !hideSuppressed,
                onToggle: () => setHideSuppressed((prev) => !prev),
                description: "Response suppressed by a Quick Suppress / Manage Response rule",
              },
              {
                label: "Disabled",
                accent: "#8C9BAB",
                visible: !hideDisabled,
                onToggle: () => setHideDisabled((prev) => !prev),
                description: "Response disabled (e.g. device in Probe Mode)",
              },
              {
                label: "Not handled",
                accent: "#E0A030",
                visible: !hideNotHandled,
                onToggle: () => setHideNotHandled((prev) => !prev),
                description: "No managed automatic response exists for this event",
              },
            ]}
          />
        )}

        {!isResponseView && (
          <SuppressionFilter
            title={isLogView ? "Log suppression" : "Suppression"}
            icon={SpeakerNotesOffOutlinedIcon}
            options={[
              {
                label: isLogView ? "Log suppressed" : "Suppressed",
                accent: "#41B145",
                visible: !hideSuppressed,
                onToggle: () => setHideSuppressed((prev) => !prev),
                description: isLogView
                  ? "Logs matching a Log Suppression rule (noise, no response involved)"
                  : "Suppressed events",
              },
            ]}
          />
        )}

        <VisibilityToggle
          label="Backup"
          icon={BackupOutlinedIcon}
          accent={palette.primary.main}
          visible={!hideBackup}
          onToggle={() => setHideBackup((prev) => !prev)}
          description="Backup / file-transfer noise (SMB writes, PowerShell, monitoring, broadcast...)"
        />

        {setHideAcked && (
          <VisibilityToggle
            label="ACK'd"
            icon={CheckCircleOutlineIcon}
            accent={palette.primary.main}
            visible={!hideAcked}
            onToggle={() => setHideAcked((prev) => !prev)}
            description="Events already acknowledged"
          />
        )}
      </Box>

      <Box sx={{ flex: 1 }} />

      <Button
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={loadingCSV}
        startIcon={loadingCSV ? <CircularProgress size={16} /> : <SaveAltIcon fontSize="small" />}
        sx={{
          color: "#eaeaea",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: "8px",
          px: 1.5,
          height: 34,
          "&:hover": { borderColor: palette.primary.light, backgroundColor: "rgba(69,144,149,0.12)" },
        }}
      >
        Export
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose(false)}>
        <MenuItem onClick={() => handleClose("csv")}>Export CSV</MenuItem>
        <MenuItem onClick={() => handleClose("json")}>Export JSON</MenuItem>
      </Menu>
    </Box>
  );
};

export default CustomToolbarWithButton;
